import type { DocumentLanguage } from '@/lib/translation/document-languages';
import clientRequest from '@/lib/client-request';

export interface PreTranslateInfo {
  detected_source_lang: string;
  source_file_name: string;
  source_file_id: string;
  source_n_chars: number;
  source_n_images: number;
  source_n_images_to_translate: number;
  source_n_tokens: number;
  translate_point_info: TranslatePointInfo;
  user_available_point: number;
  ext: string;
  page_num: number;
}

export interface TranslatePointInfo {
  point_per_batch_token: number;
  unit_batch_token: number;
  point_per_batch_image: number;
  unit_batch_image: number;
  optimize_math_formula_point: number;
  total_token_point: number;
  total_image_point: number;
}

export interface TranslateOptions {
  source_file_id: string;
  source_lang: string;
  target_lang: string;
  optimize_math_formula: boolean;
  translate_image: boolean;
  page_source: string;
}

export interface FileTranslationResponse {
  uid: string;
  title: string;
  file_translation_session_id: string;
  source_file_name: string;
  source_n_images: number;
  source_n_images_to_translate: number;
  source_n_chars: number;
  source_n_tokens: number;
  translate_point: number;
  source_lang: string;
  source_file_access_url: string;
  chat_gateway_name: string;
  optimize_math_formula: boolean;
  translate_image: boolean;
  target_lang: string;
}

export function startTranslate(
  data: TranslateOptions,
): Promise<DataResponse<FileTranslationResponse>> {
  return clientRequest.post('/file_translation/start_translate', data);
}

export type TranslationSessionStatus =
  | 'INIT'
  | 'ASYNC'
  | 'WAIT_IN_QUEUE'
  | 'PROGRESS'
  | 'DONE'
  | 'ERROR';

export interface TranslationSession {
  error_msg: string;
  uid: string;
  title: string;
  status: TranslationSessionStatus;
  ctime: string;
  mtime: string;
  file_translation_session_id: string;
  source_file_id: string;
  source_file_access_url: string;
  source_preview_file_url: string;
  source_n_images: number;
  source_n_images_to_translate: number;
  source_file_name: string;
  source_file_mime_type: string;
  source_file_size: number;
  target_file_mime_type: string;
  source_n_chars: number;
  source_n_tokens: number;
  source_lang: string;
  translate_point: number;
  target_file_id: string;
  target_file_access_url: string;
  target_preview_file_url: string;
  target_lang: string;
  target_file_name: string;
  target_file_size: number;
  optimize_math_formula: boolean;
  translate_image: boolean;
  task_name: string;
  task_progress: string;
  global_progress_percent: number;
  page_source: string;
}

export function getTranslateInfo(
  file_id: string,
): Promise<DataResponse<TranslationSession>> {
  return clientRequest.get('/file_translation/get_session_detail', {
    params: {
      file_translation_session_id: file_id,
    },
  });
}

export function retryTranslate(
  fileTranslationSessionId: string,
): Promise<DataResponse<TranslationSession>> {
  return clientRequest.post(
    `/file_translation/retry_translate/${fileTranslationSessionId}`,
  );
}

export function downloadTargetFile(url: string) {
  return clientRequest.get(url, {
    responseType: 'blob',
  });
}

export function downloadTranslateFile(
  fileTranslationSessionId: string,
): Promise<DataResponse<string>> {
  return clientRequest.post(
    `/file_translation/dual_concat_files/${fileTranslationSessionId}`,
  );
}

export interface TranslateFileUrl {
  dual_file_download_url: string;
  source_file_access_url: string;
  target_file_access_url: string;
}

export function getTranslateFileUrl(
  fileTranslationSessionId: string,
  type?: DownloadTranslateFileType,
): Promise<DataResponse<TranslateFileUrl>> {
  return clientRequest.post(
    `/file_translation/download_files/${fileTranslationSessionId}`,
    {
      type,
    },
  );
}

export type DownloadTranslateFileType = 'source' | 'target' | 'dual';

export function getTranslateHistory({
  offset,
  limit,
  keyword,
}: {
  offset: number;
  limit: number;
  keyword?: string;
}): Promise<PageInfo<TranslationSession>> {
  return clientRequest.get('/file_translation/list_session_items', {
    params: {
      offset,
      limit,
      keyword,
    },
  });
}

export function preTranslateHtml(
  url: string,
  title: string,
  html: string,
): Promise<DataResponse<PreTranslateInfo>> {
  return clientRequest.post('/file_translation/pre_translate_html', {
    url: url,
    title: title,
    html: html,
  });
}

export type LanguageItem = DocumentLanguage;

export function getLanguageList(): Promise<
  DataResponse<{
    file_translation_language: LanguageItem[];
    file_translation_language_version?: string;
  }>
> {
  return clientRequest.get('/enum/list');
}
